import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function AdminHomepage({ initial, shows, episodes, sponsors, refresh }) {
  const [f, setF] = useState({
    hero_eyebrow: initial.hero_eyebrow || "",
    hero_title_part1: initial.hero_title_part1 || "SVN",
    hero_title_part2: initial.hero_title_part2 || "NETWORK",
    tagline: initial.tagline || "Different Voices. One Signal.",
    subtitle: initial.subtitle || "",
    primary_cta_label: initial.primary_cta_label || "Watch Shows",
    primary_cta_link: initial.primary_cta_link || "/shows",
    secondary_cta_label: initial.secondary_cta_label || "Latest Episodes",
    secondary_cta_link: initial.secondary_cta_link || "/episodes",
    hero_artwork_url: initial.hero_artwork_url || "",
    weekly_lineup_title: initial.weekly_lineup_title || "Tune in every day.",
    weekly_lineup: initial.weekly_lineup?.length ? initial.weekly_lineup : [
      { day: "Monday", show_slug: "" }, { day: "Tuesday", show_slug: "" },
      { day: "Wednesday", show_slug: "" }, { day: "Thursday", show_slug: "" },
      { day: "Friday", show_slug: "" }, { day: "Saturday", show_slug: "" },
      { day: "Sunday", show_slug: "" },
    ],
    featured_show_slugs: initial.featured_show_slugs || [],
    latest_episode_ids: initial.latest_episode_ids || [],
    sponsor_spotlight_ids: initial.sponsor_spotlight_ids || [],
  });
  const [saving, setSaving] = useState(false);

  const toggle = (key, value) => {
    const list = f[key];
    setF({ ...f, [key]: list.includes(value) ? list.filter((x) => x !== value) : [...list, value] });
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/admin/homepage", f);
      toast.success("Homepage updated");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const I = (k, label) => (
    <div>
      <label className="text-[10px] font-bold uppercase tracking-[0.3em] text-neutral-400">{label}</label>
      <input value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`home-form-${k}`} className="mt-2 w-full bg-black border border-white/15 px-3 py-2 text-white focus:outline-none focus:border-gold" />
    </div>
  );

  return (
    <form onSubmit={save} className="space-y-6" data-testid="admin-homepage-form">
      <Section title="Hero">
        <div className="grid md:grid-cols-2 gap-3">
          {I("hero_eyebrow", "Eyebrow (e.g., 'Live on the Signal')")}
          {I("hero_artwork_url", "Hero artwork URL")}
          {I("hero_title_part1", "Title part 1 (white)")}
          {I("hero_title_part2", "Title part 2 (red)")}
        </div>
        {I("tagline", "Tagline")}
        <div>
          <label className="text-[10px] font-bold uppercase tracking-[0.3em] text-neutral-400">Subtitle</label>
          <textarea rows={2} value={f.subtitle} onChange={(e) => setF({ ...f, subtitle: e.target.value })} data-testid="home-form-subtitle" className="mt-2 w-full bg-black border border-white/15 px-3 py-2 text-white focus:outline-none focus:border-gold" />
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          {I("primary_cta_label", "Primary CTA label")}
          {I("primary_cta_link", "Primary CTA link")}
          {I("secondary_cta_label", "Secondary CTA label")}
          {I("secondary_cta_link", "Secondary CTA link")}
        </div>
      </Section>

      <Section title="Weekly Lineup">
        {I("weekly_lineup_title", "Lineup section title")}
        <div className="grid md:grid-cols-2 gap-3 mt-3">
          {f.weekly_lineup.map((row, idx) => (
            <div key={row.day} className="bg-black border border-white/10 p-3">
              <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold">{row.day}</p>
              <select
                value={row.show_slug || ""}
                onChange={(e) => {
                  const next = [...f.weekly_lineup];
                  next[idx] = { ...next[idx], show_slug: e.target.value };
                  setF({ ...f, weekly_lineup: next });
                }}
                data-testid={`home-lineup-${row.day.toLowerCase()}`}
                className="mt-2 w-full bg-[#0F0F0F] border border-white/15 px-3 py-2 text-white focus:outline-none focus:border-gold"
              >
                <option value="">— Empty —</option>
                {shows.map((s) => <option key={s.slug} value={s.slug}>{s.title}</option>)}
              </select>
              <input
                placeholder="Custom label (optional)"
                value={row.label_override || ""}
                onChange={(e) => {
                  const next = [...f.weekly_lineup];
                  next[idx] = { ...next[idx], label_override: e.target.value };
                  setF({ ...f, weekly_lineup: next });
                }}
                className="mt-2 w-full bg-[#0F0F0F] border border-white/15 px-3 py-2 text-white text-sm focus:outline-none focus:border-gold placeholder:text-neutral-600"
              />
            </div>
          ))}
        </div>
      </Section>

      <Section title="Featured Shows (Bento Grid)" hint="First selected gets the large hero cell. Leave empty to show all English shows.">
        <div className="grid md:grid-cols-3 gap-3">
          {shows.map((s) => {
            const checked = f.featured_show_slugs.includes(s.slug);
            return (
              <label key={s.slug} className={`flex items-center gap-3 p-3 border cursor-pointer transition ${checked ? "border-gold bg-gold/5" : "border-white/10 hover:border-white/30"}`} data-testid={`home-featured-${s.slug}`}>
                <input type="checkbox" checked={checked} onChange={() => toggle("featured_show_slugs", s.slug)} className="accent-yellow-400" />
                <img src={s.artwork_url} alt="" className="w-10 h-10 object-cover" />
                <span className="text-sm font-display font-bold text-white">{s.title}</span>
              </label>
            );
          })}
        </div>
      </Section>

      <Section title="Latest Episodes Pin" hint="Pin specific episodes here, or leave empty to auto-show the 6 newest.">
        <div className="grid md:grid-cols-2 gap-2 max-h-72 overflow-y-auto p-1">
          {episodes.map((ep) => {
            const checked = f.latest_episode_ids.includes(ep.id);
            return (
              <label key={ep.id} className={`flex items-center gap-3 p-2 border text-sm cursor-pointer ${checked ? "border-gold bg-gold/5" : "border-white/10 hover:border-white/30"}`}>
                <input type="checkbox" checked={checked} onChange={() => toggle("latest_episode_ids", ep.id)} className="accent-yellow-400" />
                <span className="truncate text-white">{ep.title}</span>
                <span className="ml-auto text-[10px] text-neutral-500 uppercase">{ep.show_slug}</span>
              </label>
            );
          })}
        </div>
      </Section>

      <Section title="Sponsor Spotlight" hint="Choose sponsors for the rotating marquee. Empty = all active sponsors with placement 'home' or 'both'.">
        <div className="grid md:grid-cols-3 gap-2">
          {sponsors.map((s) => {
            const checked = f.sponsor_spotlight_ids.includes(s.id);
            return (
              <label key={s.id} className={`flex items-center gap-3 p-2 border text-sm cursor-pointer ${checked ? "border-gold bg-gold/5" : "border-white/10 hover:border-white/30"}`}>
                <input type="checkbox" checked={checked} onChange={() => toggle("sponsor_spotlight_ids", s.id)} className="accent-yellow-400" />
                <span className="text-white">{s.name}</span>
              </label>
            );
          })}
        </div>
      </Section>

      <button type="submit" disabled={saving} data-testid="home-form-save" className="bg-svn-red text-white font-black uppercase tracking-widest text-sm px-8 py-4 hover:bg-red-700 disabled:opacity-60">
        {saving ? "Saving…" : "Save Homepage"}
      </button>
    </form>
  );
}

function Section({ title, hint, children }) {
  return (
    <div className="bg-[#0F0F0F] border border-white/10 p-6">
      <p className="text-xs font-bold uppercase tracking-[0.3em] text-gold mb-1">{title}</p>
      {hint && <p className="text-xs text-neutral-500 mb-4">{hint}</p>}
      <div className="space-y-3">{children}</div>
    </div>
  );
}
